import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Globe, ShieldCheck, AlertCircle, ExternalLink, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, tint, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${tint}`}><Icon className="h-5 w-5" /></div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function TaxOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/tax-overview`);
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load tax overview");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <Card data-testid="tax-overview-loading">
        <CardContent className="py-12 text-center text-muted-foreground">
          <Loader2 className="h-6 w-6 mx-auto mb-2 animate-spin" />
          <p>Loading tax overview...</p>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;
  const { summary, registrations, tenant_tax_ids, stripe_tax_enabled, error } = data;

  return (
    <div className="space-y-4" data-testid="tax-overview">
      {!stripe_tax_enabled && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="py-3 flex items-center gap-2 text-sm">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            <span>Stripe Tax is disabled (<code>STRIPE_TAX_ENABLED=false</code>). New checkouts will not calculate tax automatically.</span>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={Globe}
          label="Total Registrations"
          value={summary.registration_count}
          tint="bg-primary/10 text-primary"
          testid="stat-total-registrations"
        />
        <StatCard
          icon={ShieldCheck}
          label="Active Registrations"
          value={summary.active_registrations}
          tint="bg-emerald-500/10 text-emerald-600"
          testid="stat-active-registrations"
        />
        <StatCard
          icon={Globe}
          label="Tenants w/ Tax ID"
          value={summary.tenants_with_tax_id}
          tint="bg-blue-500/10 text-blue-600"
          testid="stat-tenants-with-tax-id"
        />
        <StatCard
          icon={ShieldCheck}
          label="Tax IDs Captured"
          value={summary.total_tax_ids_captured}
          tint="bg-violet-500/10 text-violet-600"
          testid="stat-total-tax-ids"
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Stripe Tax Registrations</CardTitle>
              <CardDescription>
                Jurisdictions where Business Genie is registered to collect tax
              </CardDescription>
            </div>
            <Button asChild variant="outline" size="sm" data-testid="open-stripe-tax-btn">
              <a href="https://dashboard.stripe.com/test/tax/registrations" target="_blank" rel="noreferrer">
                Manage in Stripe <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="text-sm text-amber-600 bg-amber-500/10 border border-amber-500/30 rounded p-3 mb-3">
              Could not load registrations from Stripe: {error}
            </div>
          )}
          {registrations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Globe className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p>No tax registrations yet</p>
              <p className="text-sm">Add jurisdictions in the Stripe Dashboard to start collecting tax automatically.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Country</TableHead>
                  <TableHead>Region / State</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Active From</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {registrations.map((r) => {
                  const states = Object.keys(r.country_options || {}).filter((k) => k !== r.country);
                  return (
                    <TableRow key={r.id} data-testid={`registration-row-${r.id}`}>
                      <TableCell className="font-medium uppercase">{r.country}</TableCell>
                      <TableCell className="text-sm">{states.join(", ") || "—"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          r.status === "active" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" :
                          r.status === "scheduled" ? "bg-blue-500/10 text-blue-600 border-blue-500/30" :
                          "bg-slate-500/10 text-slate-600 border-slate-500/30"
                        }>
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {r.active_from ? new Date(r.active_from * 1000).toLocaleDateString() : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tenant Tax IDs</CardTitle>
          <CardDescription>VAT / EIN / business tax IDs collected at checkout</CardDescription>
        </CardHeader>
        <CardContent>
          {tenant_tax_ids.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ShieldCheck className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p>No tenant tax IDs captured yet</p>
              <p className="text-sm">Tax IDs are collected automatically at Stripe Checkout when customers provide them.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Verification</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenant_tax_ids.map((t, i) => (
                  <TableRow key={i} data-testid={`tax-id-row-${i}`}>
                    <TableCell className="font-medium">{t.tenant_name}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{t.type}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{t.value}</TableCell>
                    <TableCell className="text-sm">{t.country || "—"}</TableCell>
                    <TableCell>
                      {t.verification === "verified" ? (
                        <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">verified</Badge>
                      ) : (
                        <Badge variant="outline">{t.verification || "pending"}</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
